import { useMemo } from 'react';
import { useCompetitions } from './useCompetitions';

export interface Team {
  id: string;
  name: string;
  color: string;
  accent: string;
  logo?: string;
}

export interface Teams {
  [key: string]: Team;
}

// NHL team colors - would come from API in production
const _TEAM_COLORS: { [key: string]: { color: string; accent: string } } = {
  // Atlantic Division
  BOS: { color: '#FFB81C', accent: '#000000' },
  BUF: { color: '#002654', accent: '#FCB514' },
  DET: { color: '#CE1126', accent: '#FFFFFF' },
  FLA: { color: '#041E42', accent: '#C8102E' },
  MTL: { color: '#AF1E2D', accent: '#192168' },
  OTT: { color: '#C52032', accent: '#C2912C' },
  TBL: { color: '#002868', accent: '#FFFFFF' },
  TOR: { color: '#00205B', accent: '#FFFFFF' },

  // Metropolitan Division
  CAR: { color: '#CC0000', accent: '#000000' },
  CBJ: { color: '#002654', accent: '#CE1126' },
  NJD: { color: '#CE1126', accent: '#000000' },
  NYI: { color: '#00539B', accent: '#F47D30' },
  NYR: { color: '#0038A8', accent: '#CE1126' },
  PHI: { color: '#F74902', accent: '#000000' },
  PIT: { color: '#000000', accent: '#FCB514' },
  WSH: { color: '#041E42', accent: '#C8102E' },

  // Central Division
  CHI: { color: '#CF0A2C', accent: '#000000' },
  COL: { color: '#6F263D', accent: '#236192' },
  DAL: { color: '#006847', accent: '#8F8F8C' },
  MIN: { color: '#A6192E', accent: '#154734' },
  NSH: { color: '#FFB81C', accent: '#041E42' },
  STL: { color: '#002F87', accent: '#FCB514' },
  WPG: { color: '#041E42', accent: '#004C97' },
  ARI: { color: '#8C2633', accent: '#E2D6B5' },

  // Pacific Division
  ANA: { color: '#F47A38', accent: '#B09862' },
  CGY: { color: '#C8102E', accent: '#F1BE48' },
  EDM: { color: '#041E42', accent: '#FF4C00' },
  LAK: { color: '#111111', accent: '#A2AAAD' },
  SJS: { color: '#006D75', accent: '#EA7200' },
  SEA: { color: '#001628', accent: '#99D9D9' },
  VAN: { color: '#00205B', accent: '#00843D' },
  VGK: { color: '#B4975A', accent: '#333F42' },
};

// Mock NHL teams for tests and development - defined outside to ensure stable reference
const MOCK_TEAMS: Teams = {
  oilers: {
    id: 'oilers',
    name: 'Oilers',
    color: '#003f7f',
    accent: '#ff6b00',
  },
  panthers: {
    id: 'panthers',
    name: 'Panthers',
    color: '#c8102e',
    accent: '#b9975b',
  },
  devils: {
    id: 'devils',
    name: 'Devils',
    color: '#CE1126',
    accent: '#000000',
  },
  penguins: {
    id: 'penguins',
    name: 'Penguins',
    color: '#000000',
    accent: '#FCB514',
  },
  team1: {
    id: 'team1',
    name: 'Team 1',
    color: '#003f7f',
    accent: '#ff6b00',
  },
  team2: {
    id: 'team2',
    name: 'Team 2',
    color: '#c8102e',
    accent: '#b9975b',
  },
};

export const useTeams = () => {
  const { competitions } = useCompetitions();

  // Extract all unique teams from competitions
  const teams: Teams = useMemo(() => {
    const teamsMap: Teams = {};

    competitions.forEach(competition => {
      competition.questions.forEach(question => {
        // For each question, extract team information if available
        if (question.teams) {
          // Teams are already extracted in useCompetitions
          // We need to get the actual team data from the API
        }
      });
    });

    // If no teams found from API, provide mock teams
    if (Object.keys(teamsMap).length === 0) {
      return MOCK_TEAMS;
    }

    return teamsMap;
  }, [competitions]);

  // Get a specific team by ID
  const getTeam = (teamId: string): Team | undefined => {
    return teams[teamId];
  };

  // Get a pair of teams
  const getTeamPair = (team1Id: string, team2Id: string): [Team, Team] => {
    const team1 = getTeam(team1Id);
    const team2 = getTeam(team2Id);

    if (!team1 || !team2) {
      const missingTeams = [];
      if (!team1) missingTeams.push(team1Id);
      if (!team2) missingTeams.push(team2Id);
      throw new Error(`Teams not found: ${team1Id}, ${team2Id}`);
    }

    return [team1, team2];
  };

  // Get teams for a specific competition
  const getCompetitionTeams = (competitionId: string): Team[] => {
    const competition = competitions.find(c => c.id === competitionId);
    if (!competition) return [];

    const teamIds = new Set<string>();
    competition.questions.forEach(q => {
      if (q.teams) {
        q.teams.forEach(teamId => teamIds.add(teamId));
      }
    });

    return Array.from(teamIds)
      .map(id => getTeam(id))
      .filter(Boolean) as Team[];
  };

  return {
    teams,
    getTeam,
    getTeamPair,
    getCompetitionTeams,
  };
};
